// Customer offer links that really work. The seller's desk publishes the offer (sell prices only, never
// cost or profit), a Worker keeps it in KV, and the customer opens it on any device, reviews it and
// confirms or declines. The decision is the audit record the seller sees come back.
//
// Pure logic, shared by both Workers (apps/web/worker.js through offers-api.ts, and the partner demo's
// worker/index.ts) and by the pages, so every side reads an offer the same way. Branding is a parameter:
// the product and the partner demo each pass their own (see SampleBrand).

export type OfferLang = 'en' | 'zh'

export interface OfferLine {
  name: string
  /** Customer price in the offer currency. */
  amount: number
}

export interface OfferDoc {
  id: string
  /** Human reference, e.g. Q-CNSHA-NLRTM-7K2. */
  ref: string
  createdAt: string
  validUntil: string
  /** The forwarder, and the network it sells through. */
  member: string
  network: string
  seller: { name: string; email?: string; wechat?: string }
  client: string
  /** "CNSHA → NLRTM". */
  route: string
  equipment?: string
  currency: string
  lines: OfferLine[]
  total: number
  /** A built-in sample: resets itself, so anyone can try the confirm flow. */
  sample?: boolean
}

export interface OfferDecision {
  kind: 'confirmed' | 'declined'
  at: string
  name: string
  company?: string
  email?: string
  /** How the customer proved who they are. */
  via: 'email' | 'wechat'
  wechatName?: string
  po?: string
  reason?: string
}

export interface OfferState {
  doc: OfferDoc
  views: string[]
  decision?: OfferDecision
  /** Who published it (the signed-in account). Kept on the server, never sent to the customer. */
  createdBy?: { userId: string; email?: string }
}

/** What any reader of an offer gets back: everything except who created it. */
export function publicOffer(s: OfferState): Omit<OfferState, 'createdBy'> {
  const { createdBy: _, ...rest } = s
  return rest
}

export type OfferStatus = 'live' | 'viewed' | 'confirmed' | 'declined' | 'expired'

export const OFFER_ID = /^q-[a-z0-9]+(?:-[a-z0-9]+){1,8}$/
export const MAX_OFFER_BYTES = 16_000
const MAX_LINES = 30
const MAX_TEXT = 120
const MAX_VIEWS = 50

const slug = (s: string) =>
  s
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/(^-|-$)/g, '')
    .toLowerCase()

/** q-cnsha-nlrtm-7k2qx: readable lane, plus a random part nobody can guess. */
export function offerId(route: string, random: string): string {
  const lane = slug(route).split('-').filter(Boolean).slice(0, 4).join('-') || 'offer'
  const tail = slug(random).replace(/-/g, '').slice(0, 6) || '0'
  return `q-${lane}-${tail}`
}

export const refOf = (id: string) => id.toUpperCase()

const text = (v: unknown, max = MAX_TEXT): string | null =>
  typeof v === 'string' && v.trim() && v.length <= max ? v.trim() : null
const optText = (v: unknown, max = MAX_TEXT): string | undefined | null =>
  v === undefined || v === null || v === '' ? undefined : text(v, max)
const money = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 && v < 10_000_000
    ? Math.round(v * 100) / 100
    : null
const isoDate = (v: unknown): string | null =>
  typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : null

/**
 * What the seller's desk may publish. Everything is plain text the page renders as text: no links, no
 * markup, short fields, so the domain cannot be turned into somebody else's page.
 */
export function readDraft(
  x: unknown
): Omit<OfferDoc, 'id' | 'ref' | 'createdAt' | 'sample'> | { error: string } {
  if (!x || typeof x !== 'object') return { error: 'offer must be an object' }
  const o = x as Record<string, unknown>
  const seller = (o.seller ?? {}) as Record<string, unknown>
  const lines = Array.isArray(o.lines) ? o.lines : null
  if (!lines || !lines.length || lines.length > MAX_LINES)
    return { error: `lines: 1 to ${MAX_LINES}` }
  const read: OfferLine[] = []
  for (const l of lines) {
    const r = (l ?? {}) as Record<string, unknown>
    const name = text(r.name)
    const amount = money(r.amount)
    if (name === null || amount === null) return { error: 'each line needs a name and an amount' }
    read.push({ name, amount })
  }
  const fields = {
    validUntil: isoDate(o.validUntil),
    member: text(o.member, 60),
    network: text(o.network, 60),
    sellerName: text(seller.name, 80),
    sellerEmail: optText(seller.email, 120),
    sellerWechat: optText(seller.wechat, 60),
    client: text(o.client, 120),
    route: text(o.route, 80),
    equipment: optText(o.equipment, 60),
    currency: typeof o.currency === 'string' && /^[A-Z]{3}$/.test(o.currency) ? o.currency : null,
  }
  for (const [k, v] of Object.entries(fields))
    if (v === null) return { error: `${k} is missing or too long` }
  const total = Math.round(read.reduce((a, l) => a + l.amount, 0) * 100) / 100
  return {
    validUntil: fields.validUntil as string,
    member: fields.member as string,
    network: fields.network as string,
    seller: {
      name: fields.sellerName as string,
      ...(fields.sellerEmail ? { email: fields.sellerEmail } : {}),
      ...(fields.sellerWechat ? { wechat: fields.sellerWechat } : {}),
    },
    client: fields.client as string,
    route: fields.route as string,
    ...(fields.equipment ? { equipment: fields.equipment } : {}),
    currency: fields.currency as string,
    lines: read,
    total,
  }
}

/** The customer's decision, read as strictly as the offer. */
export function readDecision(x: unknown, at: string): OfferDecision | { error: string } {
  if (!x || typeof x !== 'object') return { error: 'decision must be an object' }
  const o = x as Record<string, unknown>
  if (o.kind !== 'confirmed' && o.kind !== 'declined') return { error: 'kind' }
  if (o.via !== 'email' && o.via !== 'wechat') return { error: 'via' }
  const name = text(o.name, 80)
  if (!name) return { error: 'name' }
  const email = optText(o.email, 120)
  if (email === null || (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)))
    return { error: 'email' }
  if (o.via === 'email' && !email) return { error: 'email' }
  const company = optText(o.company, 120)
  const wechatName = optText(o.wechatName, 60)
  const po = optText(o.po, 60)
  const reason = optText(o.reason, 300)
  if (company === null || wechatName === null || po === null || reason === null)
    return { error: 'a field is too long' }
  return {
    kind: o.kind,
    at,
    name,
    via: o.via,
    ...(company ? { company } : {}),
    ...(email ? { email } : {}),
    ...(wechatName ? { wechatName } : {}),
    ...(po ? { po } : {}),
    ...(reason ? { reason } : {}),
  }
}

export function offerStatus(s: OfferState, now: Date): OfferStatus {
  if (s.decision) return s.decision.kind
  if (Date.parse(s.doc.validUntil) < now.getTime()) return 'expired'
  return s.views.length ? 'viewed' : 'live'
}

/** A view is counted once a minute at most, and the list stays short. */
export function withView(s: OfferState, at: string): OfferState {
  const last = s.views.at(-1)
  if (last && Date.parse(at) - Date.parse(last) < 60_000) return s
  return { ...s, views: [...s.views, at].slice(-MAX_VIEWS) }
}

/** One decision per offer, and none on an offer past its validity. */
export function decide(s: OfferState, d: OfferDecision): OfferState | { error: string } {
  if (s.decision) return { error: `already ${s.decision.kind}` }
  if (Date.parse(s.doc.validUntil) < Date.parse(d.at)) return { error: 'expired' }
  return { ...s, decision: d }
}

const DAY = 864e5

/** Built-in samples, so a link always works, on any device, even before anyone has sent an offer. */
export const SAMPLE_IDS = ['q-sample-cnsha-nlrtm', 'q-sample-cnngb-uslax', 'q-sample-vnsgn-deham']

/** Who the built-in samples come from: each app passes its own forwarder, network and seller. */
export interface SampleBrand {
  member: string
  network: string
  seller: { name: string; email?: string; wechat?: string }
}

/** The product's samples: a neutral sample forwarder sending through FreightRatesMCP. */
export const PRODUCT_SAMPLE_BRAND: SampleBrand = {
  member: 'Harbour Line Logistics',
  network: 'FreightRatesMCP',
  seller: { name: 'Mira Jansen', email: 'mira.jansen@harbourline.example' },
}

export function sampleOffer(id: string, now: Date, brand: SampleBrand): OfferDoc | null {
  const base = {
    member: brand.member,
    network: brand.network,
    seller: { ...brand.seller },
    createdAt: new Date(now.getTime() - 2 * 3600e3).toISOString(),
    validUntil: new Date(now.getTime() + 14 * DAY).toISOString(),
    currency: 'USD',
    sample: true,
  }
  const make = (
    client: string,
    route: string,
    equipment: string,
    lines: OfferLine[]
  ): OfferDoc => ({
    ...base,
    id,
    ref: refOf(id),
    client,
    route,
    equipment,
    lines,
    total: Math.round(lines.reduce((a, l) => a + l.amount, 0) * 100) / 100,
  })
  switch (id) {
    case 'q-sample-cnsha-nlrtm':
      return make('Northstar Projects', 'CNSHA → NLRTM', '2 × 40HC', [
        { name: 'Ocean freight, 2 × 40HC', amount: 6280 },
        { name: 'Origin terminal handling', amount: 420 },
        { name: 'Export customs clearance', amount: 95 },
        { name: 'Bill of lading', amount: 65 },
        { name: 'Destination terminal handling', amount: 540 },
      ])
    case 'q-sample-cnngb-uslax':
      return make('Hearth & Field', 'CNNGB → USLAX', '1 × 40HC', [
        { name: 'Ocean freight, 1 × 40HC', amount: 2950 },
        { name: 'Origin terminal handling', amount: 210 },
        { name: 'AMS and ISF filing', amount: 85 },
        { name: 'Destination terminal handling', amount: 395 },
      ])
    case 'q-sample-vnsgn-deham':
      return make('Aster Machinery', 'VNSGN → DEHAM', '3 × 20GP', [
        { name: 'Ocean freight, 3 × 20GP', amount: 4410 },
        { name: 'Origin terminal handling', amount: 360 },
        { name: 'Export customs clearance', amount: 95 },
        { name: 'Destination terminal handling', amount: 630 },
        { name: 'Delivery to Hamburg warehouse', amount: 540 },
      ])
    default:
      return null
  }
}

/** The address of an offer's customer page on the member's own desk. */
export const offerUrl = (origin: string, id: string) => `${origin.replace(/\/$/, '')}/offer/${id}`
